import {
  createContext,
  ReactNode,
  useCallback,
  use,
  useMemo,
  useState,
} from "react";
import { useTenant } from "hooks/settings";
import {
  ScrapEyeInstallationVideoPlayerGroupWithAngles,
  ScrapEyeRecordingPosition,
  useListVideoPlayerGroupsQuery,
} from "src/store/api/generatedApi";

interface VideoPlayerGroupContextType {
  location: string;
  videoPlayerGroups: readonly ScrapEyeInstallationVideoPlayerGroupWithAngles[];
  currentAngleIndices: Record<number, number>;
  cycleAllAngles: () => void;
  resetAllToAuto: () => void;
  setAngleIndexForGroup: (groupId: number, angleIndex: number) => void;
  currentAnglesForDisplay: readonly (ScrapEyeRecordingPosition & {
    groupId: number;
  })[];
  isLoading: boolean;
}

export const VideoPlayerGroupContext =
  createContext<VideoPlayerGroupContextType | null>(null);

export const useVideoPlayerGroupContext = (): VideoPlayerGroupContextType => {
  const context = use(VideoPlayerGroupContext);
  if (!context) {
    throw new Error(
      "useVideoPlayerGroupContext must be used within VideoPlayerGroupProvider"
    );
  }
  return context;
};

export const useVideoPlayerGroupContextOptional =
  (): VideoPlayerGroupContextType | null => {
    return use(VideoPlayerGroupContext);
  };

type VideoPlayerGroupProviderProps = Readonly<{
  children: ReactNode;
  location: string;
}>;

export const VideoPlayerGroupProvider = ({
  children,
  location,
}: VideoPlayerGroupProviderProps) => {
  const context = use(VideoPlayerGroupContext);
  if (context !== null && context.location === location) {
    return children;
  }
  return (
    <InnerVideoPlayerGroupProvider location={location}>
      {children}
    </InnerVideoPlayerGroupProvider>
  );
};

const InnerVideoPlayerGroupProvider = ({
  children,
  location,
}: VideoPlayerGroupProviderProps) => {
  const tenantName = useTenant();

  const { data: videoPlayerGroups, isLoading } = useListVideoPlayerGroupsQuery({
    tenantName,
    locationName: location,
  });

  const [currentAngleIndices, setCurrentAngleIndices] = useState<
    Record<number, number>
  >({});

  const cycleAllAngles = useCallback(() => {
    if (!videoPlayerGroups) return;
    setCurrentAngleIndices((prev) => {
      const next: Record<number, number> = {};

      for (const group of videoPlayerGroups) {
        if (group.angles.length === 0) {
          continue;
        }

        const currentIndex = prev[group.id] ?? 0;
        const nextIndex = (currentIndex + 1) % group.angles.length;
        next[group.id] = nextIndex;
      }

      return { ...next };
    });
  }, [videoPlayerGroups]);

  const resetAllToAuto = useCallback(() => {
    setCurrentAngleIndices({});
  }, []);

  const setAngleIndexForGroup = useCallback(
    (groupId: number, angleIndex: number) => {
      setCurrentAngleIndices((prev) => ({
        ...prev,
        [groupId]: angleIndex,
      }));
    },
    []
  );

  const currentAnglesForDisplay = useMemo(() => {
    if (!videoPlayerGroups) return [];
    return videoPlayerGroups
      .map((group) => {
        if (group.angles.length === 0) {
          return null;
        }

        const currentIndex = currentAngleIndices[group.id] ?? 0;
        const angle = group.angles[currentIndex];

        if (!angle) {
          return null;
        }

        return {
          groupId: group.id,
          ...angle,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [videoPlayerGroups, currentAngleIndices]);

  const value = useMemo<VideoPlayerGroupContextType>(
    () => ({
      videoPlayerGroups: videoPlayerGroups ?? [],
      currentAngleIndices,
      cycleAllAngles,
      resetAllToAuto,
      setAngleIndexForGroup,
      currentAnglesForDisplay,
      isLoading,
      location,
    }),
    [
      videoPlayerGroups,
      currentAngleIndices,
      cycleAllAngles,
      resetAllToAuto,
      setAngleIndexForGroup,
      currentAnglesForDisplay,
      isLoading,
      location,
    ]
  );

  return (
    <VideoPlayerGroupContext value={value}>{children}</VideoPlayerGroupContext>
  );
};
